import { DecimalPipe, getCurrencySymbol, NgIf, AsyncPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDivider } from '@angular/material/list';
import { RootState } from '@next/core-lib/app';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { FeedProfitCalculations } from 'src/app/feed-profit/services/feed-profit-calculations.service';

import { FeedProfitExtendedSimulationsCalculationService } from 'src/app/feed-profit/services/feed-profit-extended-simulations-calculation.service';

import { FeedProfitQuickSimulationsCalculationService } from 'src/app/feed-profit/services/feed-profit-quick-simulations-calculation.service';
import { Discount } from 'src/app/settings/models/settings.model';
import { getDiscount } from 'src/app/settings/store/selectors/settings.selectors';
import Utils from 'src/app/utils';
import { FEED_PROFIT_ENERGY_VALUES } from 'src/assets/data/feedProfitConstants';

import { BaselineDelivery, Calculation, FeederType } from '../../../models/feed-profit.model';

export enum EnergyTypes {
  startLessThan25 = 'startLessThan25',
  startMoreThan25 = 'startMoreThan25',
  endBoar = 'endBoar',
  gift = 'gift',
  feedGift = 'feedGift',
}

@Component({
  selector: 'app-extended-baseline-settings',
  templateUrl: './extended-baseline-settings.component.html',
  styleUrls: ['./extended-baseline-settings.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    MatPrefix,
    NgIf,
    MatDivider,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ExtendedBaselineSettingsComponent implements OnInit, OnDestroy {
  @Input() isExtendedSimulation: boolean;
  @Input() calculation: Calculation;

  currencySymbol = this.localeService.getLocationCurrencySymbol();
  invalidPercentages = false;
  discounts: Discount;
  subscriptions: Subscription[] = [];

  fertilizerPerGrowFinisherInCubicMeters$: Observable<number>;
  thirdDeliveryPercentage$: Observable<number>;

  public get energyTypes(): typeof EnergyTypes {
    return EnergyTypes;
  }

  form = new FormGroup({
    squareMeterPerGrowFinisher: new FormControl<number>(null, []),
    amountOfGrowFinisher: new FormControl<number>(null, []),
    startWeight: new FormControl<number>(null, []),
    growFinisherPrice: new FormControl<number>(null, []),
    revenuePerKg: new FormControl<number>(null, []),
    feedPackagePrice: new FormControl<number>(null, []),
    slaughteredWeightInKg: new FormControl<number>(null, []),
    meatPercentage: new FormControl<number>(null, []),
    loinDepthThicknessInMillimeters: new FormControl<number>(null, []),
    backFatThicknessInMillimeters: new FormControl<number>(null, []),
    growth: new FormControl<number>(null, []),
    averageEnergyConversion: new FormControl<number>(null, []),
    mortalityPercentage: new FormControl<number>(null, []),
    cleaning: new FormControl<number>(null, []),
    fertilizerPerGrowFinisherInCubicMeters: new FormControl<number>(null, []),
    fertilizerPricePerCubicMeter: new FormControl<number>(null, []),
    firstDeliveryPercentage: new FormControl<number>(null, []),
    secondDeliveryDays: new FormControl<number>(null, []),
    secondDeliveryPercentage: new FormControl<number>(null, []),
    thirdDeliveryDays: new FormControl<number>(null, []),
  });

  constructor(
    private readonly localeService: LocaleService,
    private readonly genericCalculationsService: FeedProfitCalculations,
    private readonly quickCalculationsService: FeedProfitQuickSimulationsCalculationService,
    private readonly extendedCalculationsService: FeedProfitExtendedSimulationsCalculationService,
    private readonly decimalPipe: DecimalPipe,
    private readonly store: Store<RootState>,
  ) {}

  initialLoad = true;
  ngOnInit() {
    this.currencySymbol = this.currencySymbol = getCurrencySymbol(
      this.localeService.getCustomerCurrency(),
      'narrow',
    );
    this.setTableValues(this.calculation);
    this.form.controls.fertilizerPerGrowFinisherInCubicMeters.disable();

    this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.calculation = this.getCalculation();
      this.form.controls.fertilizerPerGrowFinisherInCubicMeters.setValue(
        this.calculation.extendedBaseLine?.fertilizerPerGrowFinisherInCubicMeters,
        {
          emitEvent: false,
        },
      );
    });

    this.thirdDeliveryPercentage$ = this.form.valueChanges.pipe(
      distinctUntilChanged(),
      startWith(100),
      map((_calculation) => this.calculate3rdDeliveryPercentage()),
    );

    this.calculateM3Fertilizer(this.calculation);

    this.subscriptions.push(
      this.store.select(getDiscount).subscribe((discounts) => (this.discounts = discounts)),
    );
    this.initialLoad = false;
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((s) => s.unsubscribe());
    }
  }

  setTableValues(calculation: Calculation) {
    const extendedBaseLine = this.isExtendedSimulation
      ? calculation?.extendedSimulation?.extendedBaseLine
      : calculation?.extendedBaseLine;

    this.controlledFormSetNumber(
      this.form.controls.squareMeterPerGrowFinisher,
      extendedBaseLine?.squareMeterPerGrowFinisher,
    );

    this.controlledFormSetNumber(
      this.form.controls.amountOfGrowFinisher,
      calculation?.companyDetails?.amountOfGrowFinisher,
    );
    this.controlledFormSetNumber(this.form.controls.startWeight, extendedBaseLine?.startWeight);
    this.controlledFormSetNumber(
      this.form.controls.growFinisherPrice,
      extendedBaseLine?.growFinisherPrice,
    );
    this.controlledFormSetNumber(this.form.controls.revenuePerKg, extendedBaseLine?.revenuePerKg);
    this.controlledFormSetNumber(
      this.form.controls.slaughteredWeightInKg,
      extendedBaseLine?.slaughteredWeightInKg,
    );
    this.controlledFormSetNumber(
      this.form.controls.meatPercentage,
      extendedBaseLine?.meatPercentage,
    );
    if (this.isExtendedSimulation) {
      this.controlledFormSetNumber(
        this.form.controls.feedPackagePrice,
        calculation?.extendedSimulation?.extendedBaseLine?.feedPackagePrice,
      );
    }
    this.controlledFormSetNumber(
      this.form.controls.loinDepthThicknessInMillimeters,
      extendedBaseLine?.loinDepthThicknessInMillimeters,
    );
    this.controlledFormSetNumber(
      this.form.controls.backFatThicknessInMillimeters,
      extendedBaseLine?.backFatThicknessInMillimeters,
    );
    this.controlledFormSetNumber(this.form.controls.growth, extendedBaseLine?.growth);
    this.controlledFormSetNumber(
      this.form.controls.averageEnergyConversion,
      extendedBaseLine?.averageEnergyConversion,
    );
    this.controlledFormSetNumber(
      this.form.controls.mortalityPercentage,
      extendedBaseLine?.mortalityPercentage,
    );
    this.controlledFormSetNumber(this.form.controls.cleaning, extendedBaseLine?.cleaning);
    this.controlledFormSetNumber(this.form.controls.cleaning, extendedBaseLine?.cleaning);
    this.controlledFormSetNumber(
      this.form.controls.fertilizerPricePerCubicMeter,
      extendedBaseLine?.fertilizerPricePerCubicMeter,
    );
    if (!!extendedBaseLine?.deliveries && extendedBaseLine?.deliveries.length > 0) {
      this.controlledFormSetNumber(
        this.form.controls.firstDeliveryPercentage,
        extendedBaseLine?.deliveries[0].percentage,
      );
      this.controlledFormSetNumber(
        this.form.controls.secondDeliveryDays,
        extendedBaseLine?.deliveries[1].days,
      );
      this.controlledFormSetNumber(
        this.form.controls.secondDeliveryPercentage,
        extendedBaseLine?.deliveries[1].percentage,
      );
      this.controlledFormSetNumber(
        this.form.controls.thirdDeliveryDays,
        extendedBaseLine?.deliveries[2].days,
      );
    }

    // Calculate initial values and disable user input since this should always be calculated
    const M3Fertilizer = this.fertilizerPerGrowFinisherInCubicMetersCanBeCalculated(calculation)
      ? this.calculateM3Fertilizer(calculation)
      : 0;

    this.controlledFormSetNumber(
      this.form.controls.fertilizerPerGrowFinisherInCubicMeters,
      M3Fertilizer,
    );
  }

  controlledFormSetNumber(formControl: FormControl<number>, value: number) {
    if (Utils.shouldUpdateNumericForm(formControl.value, value)) {
      formControl.setValue(value, { emitEvent: !this.initialLoad });
    }
  }

  calculate3rdDeliveryPercentage() {
    const remainingPercentage =
      100 -
      Number(this.form.controls.firstDeliveryPercentage.value) -
      Number(this.form.controls.secondDeliveryPercentage.value);
    if (remainingPercentage < 0) {
      this.invalidPercentages = true;
      this.form.controls.firstDeliveryPercentage.setErrors(<ValidationErrors>[
        { remainingPercentageError: true },
      ]);
      this.form.controls.secondDeliveryPercentage.setErrors(<ValidationErrors>[
        { remainingPercentageError: true },
      ]);
    } else {
      this.invalidPercentages = false;
      this.form.controls.firstDeliveryPercentage.setErrors(null);
      this.form.controls.secondDeliveryPercentage.setErrors(null);
    }
    return remainingPercentage;
  }

  calculateM3Fertilizer(calculation: Calculation) {
    let fertilizerPerGrowFinisherInCubicMeters = 0;
    if (this.isExtendedSimulation) {
      // make sure we calculate feedpackageprice ahead of time
      calculation = {
        ...calculation,
        extendedSimulation: {
          ...calculation.extendedSimulation,
          extendedBaseLine: {
            ...calculation.extendedSimulation?.extendedBaseLine,
            feedPackagePrice: this.extendedCalculationsService.getFeedPackagePrice(
              calculation.extendedSimulation,
            ),
          },
        },
      };
    }

    if (this.fertilizerPerGrowFinisherInCubicMetersCanBeCalculated(calculation)) {
      if (this.isExtendedSimulation) {
        fertilizerPerGrowFinisherInCubicMeters =
          this.extendedCalculationsService.getManureCubeProduction(
            calculation,
            this.extendedCalculationsService.getSimulation(
              calculation,
              calculation.extendedSimulation,
            ),
            true,
          );
      } else {
        const comparisonCalculation = this.quickCalculationsService.generateMappedCalculation(
          calculation,
          this.quickCalculationsService.getComparisonSimulation(calculation, this.discounts),
          this.discounts,
        );

        fertilizerPerGrowFinisherInCubicMeters =
          this.quickCalculationsService.getManureCubeProduction(
            comparisonCalculation,
            comparisonCalculation.comparisonSimulation,
          );
      }
    } else {
      fertilizerPerGrowFinisherInCubicMeters = this.isExtendedSimulation
        ? calculation?.extendedSimulation?.extendedBaseLine?.fertilizerPerGrowFinisherInCubicMeters
        : calculation?.extendedBaseLine?.fertilizerPerGrowFinisherInCubicMeters;
    }

    if (
      fertilizerPerGrowFinisherInCubicMeters === null ||
      fertilizerPerGrowFinisherInCubicMeters === undefined ||
      isNaN(fertilizerPerGrowFinisherInCubicMeters) ||
      !isFinite(fertilizerPerGrowFinisherInCubicMeters)
    ) {
      return 0;
    }
    return fertilizerPerGrowFinisherInCubicMeters;
  }

  fertilizerPerGrowFinisherInCubicMetersCanBeCalculated(calculation: Calculation) {
    const inputValue = this.isExtendedSimulation ? calculation.extendedSimulation : calculation;
    const hasComparison = this.isExtendedSimulation
      ? calculation?.extendedBaseLine?.startWeight != null &&
        calculation?.extendedComparisonSimulation?.startWeight != null
      : calculation?.extendedBaseLine?.startWeight != null;

    return (
      inputValue?.feeders?.length > 0 &&
      hasComparison &&
      inputValue.baseLine != null &&
      Object.values(inputValue.baseLine).every((val) => val != null) &&
      inputValue.extendedBaseLine != null &&
      Object.entries(inputValue.extendedBaseLine).every((entry) =>
        entry[0] === 'fertilizerPerGrowFinisherInCubicMeters' ? true : entry[1] != null,
      )
    );
  }

  getValue(value: number, digitsInfo = '1.0-3') {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return this.localeService.translate('n/a');
    }
    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  getCalculation() {
    const {
      squareMeterPerGrowFinisher,
      startWeight,
      growFinisherPrice,
      revenuePerKg,
      slaughteredWeightInKg,
      meatPercentage,
      loinDepthThicknessInMillimeters,
      backFatThicknessInMillimeters,
      fertilizerPerGrowFinisherInCubicMeters,
      growth,
      averageEnergyConversion,
      mortalityPercentage,
      cleaning,
      fertilizerPricePerCubicMeter,
      firstDeliveryPercentage,
      secondDeliveryDays,
      secondDeliveryPercentage,
      thirdDeliveryDays,
      amountOfGrowFinisher,
    } = this.form.value;

    const newExtendedBaseline = {
      squareMeterPerGrowFinisher,
      amountOfGrowFinisher,
      startWeight,
      growFinisherPrice,
      revenuePerKg,
      slaughteredWeightInKg,
      meatPercentage,
      loinDepthThicknessInMillimeters,
      backFatThicknessInMillimeters,
      growth,
      averageEnergyConversion,
      mortalityPercentage,
      cleaning,
      fertilizerPerGrowFinisherInCubicMeters,
      fertilizerPricePerCubicMeter,
      deliveries: <BaselineDelivery[]>[
        { index: 0, days: 0, percentage: firstDeliveryPercentage },
        {
          index: 1,
          days: secondDeliveryDays,
          percentage: secondDeliveryPercentage,
        },
        {
          index: 2,
          days: thirdDeliveryDays,
          percentage: 100 - firstDeliveryPercentage - secondDeliveryPercentage,
        },
      ],
    };

    //build the initial object with the newest values
    let newCalculation: Calculation = this.isExtendedSimulation
      ? {
          ...this.calculation,
          extendedSimulation: {
            ...this.calculation.extendedSimulation,
            extendedBaseLine: {
              ...newExtendedBaseline,
              feedPackagePrice: null,
            },
          },
        }
      : {
          ...this.calculation,
          extendedBaseLine: newExtendedBaseline,
        };

    // Add newest comparisons if they can be calculated.
    if (
      (this.isExtendedSimulation,
      this.genericCalculationsService.getExtendedComparisonCanBeCalculated(newCalculation))
    ) {
      newCalculation.extendedComparisonSimulation =
        this.extendedCalculationsService.getComparisonSimulation(newCalculation);
    } else if (this.genericCalculationsService.getQuickSimulationCanBeCalculated(newCalculation)) {
      newCalculation.comparisonSimulation = this.quickCalculationsService.getComparisonSimulation(
        newCalculation,
        this.discounts,
      );
    }

    // Calculate newest fertilizer values.
    const newFertilizerPerGrowFinisherInCubicMeters = this.calculateM3Fertilizer(newCalculation);

    // Add calculated values, add feedpackagePrice if neccesairy.
    newCalculation = this.isExtendedSimulation
      ? {
          ...this.calculation,
          extendedSimulation: {
            ...this.calculation.extendedSimulation,
            extendedBaseLine: {
              ...newExtendedBaseline,
              feedPackagePrice:
                this.extendedCalculationsService.getFeedCostTotalCostTotalKgForPacketPriceForFeedCosts(
                  newCalculation.extendedSimulation,
                ).feedcosts,
              fertilizerPerGrowFinisherInCubicMeters: newFertilizerPerGrowFinisherInCubicMeters,
            },
          },
        }
      : {
          ...this.calculation,
          extendedBaseLine: {
            ...newExtendedBaseline,
            fertilizerPerGrowFinisherInCubicMeters: newFertilizerPerGrowFinisherInCubicMeters,
          },
        };

    this.controlledFormSetNumber(
      this.form.controls.fertilizerPerGrowFinisherInCubicMeters,
      newFertilizerPerGrowFinisherInCubicMeters,
    );
    return newCalculation;
  }

  getEnergyGiftValues(energyType: EnergyTypes, calculation: Calculation) {
    const EnergyconversionValue = this.getEnergyConversionValues(energyType, calculation);
    return EnergyconversionValue * (this.calculation?.baseLine?.growthStartProcedure / 1000);
  }

  getFeedGiftValues(energyType: EnergyTypes, calculation: Calculation) {
    const feeders = this.isExtendedSimulation
      ? this.calculation?.extendedSimulation?.feeders
      : this.calculation?.feeders;
    if (feeders?.length > 0) {
      const energyGift = this.getEnergyGiftValues(energyType, calculation);
      return (
        energyGift /
        feeders?.filter((feeder) => feeder.type === FeederType.START_FEEDER)[0]?.product?.ewKg // TODO Correct ??
      );
    }
    return null;
  }

  roundEnergyValue(value: number, digitsInfo = '1.0-2') {
    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  getEnergyConversionValues(energyType: EnergyTypes, calculation: Calculation) {
    const extendedBaseline = this.isExtendedSimulation
      ? calculation?.extendedSimulation?.extendedBaseLine
      : calculation?.extendedBaseLine;

    if (extendedBaseline != null) {
      const energyReturnValues =
        energyType === EnergyTypes.startLessThan25
          ? FEED_PROFIT_ENERGY_VALUES.values.startLessThan25
          : energyType === EnergyTypes.endBoar
            ? FEED_PROFIT_ENERGY_VALUES.values.endBoar
            : FEED_PROFIT_ENERGY_VALUES.values.startMoreThan25;
      return this.genericCalculationsService.getEnergyValueFromLookup(
        extendedBaseline.averageEnergyConversion,
        energyReturnValues,
      );
    }
  }
}
